<script setup lang="ts">
import { useSeo } from '@/composables/useSeo'
import PageLayout from '@/components/layout/PageLayout.vue'
import Card from '@/components/ui/Card.vue'
import Icon from '@/components/icons/Icon.vue'

useSeo({
  title: 'Terms of Use',
  description:
    'Terms of use for ClearRand: free calculators, outputs are estimates and not advice, provided without warranty.',
  path: '/terms',
})

const terms = [
  {
    icon: 'calculator' as const,
    title: 'Free to use',
    description: 'Use the calculators for personal and business planning.',
  },
  {
    icon: 'info' as const,
    title: 'Estimates only',
    description: 'Outputs are educational tools, not professional advice.',
  },
  {
    icon: 'exclamation' as const,
    title: 'Copyright protected',
    description: 'Do not scrape, redistribute or republish content without permission.',
  },
]
</script>

<template>
  <PageLayout
    title="Terms of use"
    description="The terms that apply when using ClearRand."
    :breadcrumbs="[{ label: 'Terms' }]"
  >
    <div class="section-heading">
      <span class="section-heading__eyebrow">Summary</span>
      <h2 class="section-heading__title">Terms at a glance</h2>
    </div>
    <div class="terms-grid">
      <Card v-for="item in terms" :key="item.title" padding="lg" raised accent="primary" hover>
        <div class="terms-card__icon">
          <Icon :name="item.icon" :size="24" />
        </div>
        <h2 class="terms-card__title">{{ item.title }}</h2>
        <p class="terms-card__desc">{{ item.description }}</p>
      </Card>
    </div>

    <article class="prose">
      <p><strong>Last reviewed:</strong> 11 September 2026</p>
      <h2>Use of the service</h2>
      <p>
        ClearRand is provided free of charge for personal and business planning. You may use the
        calculators and read the content for lawful purposes.
      </p>
      <h2>No advice</h2>
      <p>
        The outputs are estimates and educational tools only. They do not replace professional
        financial, tax, legal, lending or technical advice. We are not responsible for decisions
        made based on calculator outputs.
      </p>
      <h2>Intellectual property</h2>
      <p>
        The content, design and code of this site are protected by copyright. You may not scrape,
        redistribute or republish the calculators or data without permission.
      </p>
    </article>
  </PageLayout>
</template>

<style scoped>
.section-heading {
  margin-bottom: var(--space-5);
}

.section-heading__eyebrow {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
}

.section-heading__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  margin: 0;
}

.terms-grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: 1fr;
  margin-bottom: var(--space-10);
}

@media (min-width: 640px) {
  .terms-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.terms-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgb(10 92 86 / 0.12), rgb(13 148 136 / 0.08));
  color: var(--color-primary);
  margin-bottom: var(--space-4);
}

.terms-card__title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text);
}

.terms-card__desc {
  margin-top: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
}

.prose {
  max-width: 70ch;
  color: var(--color-text);
  line-height: var(--leading-relaxed);
}

.prose h2 {
  margin-top: var(--space-8);
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
}

.prose p {
  margin-top: var(--space-3);
}
</style>
